import React from 'react';
import { InitialAvatar } from '../InitialAvatar';
import { CURRENT_MEMBER, TIMELINE_END, TIMELINE_SPAN, TIMELINE_START, overlapWith, parseYears, publicName } from '../../data/community';
import type { Member } from '../../data/community';
interface YearsChartProps {
  members: Member[];
  selected: Member | null;
  onSelect: (member: Member) => void;
}
const TICKS = [1976, 1986, 1996, 2006, 2016, 2026];
function position(years: string) {
  const {
    start,
    end
  } = parseYears(years);
  const left = (start - TIMELINE_START) / TIMELINE_SPAN * 100;
  const width = Math.max((end - start) / TIMELINE_SPAN * 100, 1.5);
  return {
    left: `${left}%`,
    width: `${width}%`
  };
}
const you = parseYears(CURRENT_MEMBER.years);
const yourBand = {
  left: `${(you.start - TIMELINE_START) / TIMELINE_SPAN * 100}%`,
  width: `${(you.end - you.start) / TIMELINE_SPAN * 100}%`
};

/**
 * Fifty years across, one member per row. Overlap with the signed-in member is
 * spatial — you see who was there when you were rather than reading a count.
 */
export function YearsChart({
  members,
  selected,
  onSelect
}: YearsChartProps) {
  const ordered = [...members].sort((a, b) => parseYears(a.years).start - parseYears(b.years).start);
  return <div className="flex flex-col" data-id="element-309" data-mp="8flqtt5yw2jn">
      <div className="sticky top-0 z-[5] flex items-end gap-3 border-b border-rule bg-card pb-2 pt-3" data-id="element-310" data-mp="cg5nywqjj65s">
        <span className="w-24 shrink-0 text-eyebrow-caption font-semibold uppercase text-ink-muted sm:w-36" data-id="element-311" data-mp="3bvstbwcnbtqu">
          {ordered.length} people
        </span>
        <div className="relative h-4 flex-1" data-id="element-312" data-mp="3guvu34llfdt0">
          {TICKS.map(tick => <span key={tick} className="absolute -translate-x-1/2 text-eyebrow-caption font-medium text-ink-muted" style={{
          left: `${(tick - TIMELINE_START) / TIMELINE_SPAN * 100}%`
        }} data-id="element-313" data-mp="3kuo3fjit9nzf">
              {tick === TIMELINE_END ? 'now' : tick}
            </span>)}
        </div>
      </div>

      <ol className="relative flex flex-col" data-id="element-314" data-mp="4bwle1qv2hq2">
        <div aria-hidden="true" className="pointer-events-none absolute bottom-0 top-0 ml-24 hidden sm:ml-36 sm:block" style={{
        left: 0,
        right: 0
      }} data-id="element-315" data-mp="4alfnwduuzwb">
          <div className="relative h-full w-full" data-id="element-316" data-mp="e0o7vud5k9tl">
            <span className="absolute top-0 h-full rounded-sm bg-primary-soft" style={yourBand} data-id="element-317" data-mp="92wdmtp1cbm8" />
          </div>
        </div>

        {ordered.map(member => {
        const overlap = overlapWith(member, CURRENT_MEMBER);
        const isYou = member.name === CURRENT_MEMBER.fullName;
        const isSelected = selected?.name === member.name;
        return <li key={member.name} className="relative" data-id="element-318" data-mp="3mi17e7kiyag4">
              <button type="button" onClick={() => onSelect(member)} aria-pressed={isSelected} className={`flex min-h-tap w-full items-center gap-3 rounded-field px-1 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 ${isSelected ? 'bg-canvas' : 'hover:bg-canvas'}`} data-id="element-319" data-mp="3mgho083knja4">
                <span className="flex w-24 shrink-0 items-center gap-2 sm:w-36" data-id="element-320" data-mp="zdykatjn8hot">
                  <InitialAvatar name={member.name} size={24} data-id="element-321" data-mp="14bgj3r3y5gj3" />
                  <span className={`truncate text-small ${isYou ? 'font-semibold text-primary-text' : 'text-ink'}`} data-id="element-322" data-mp="14dtaeuvlh38t">
                    {isYou ? 'You' : publicName(member.name)}
                  </span>
                </span>

                <span className="relative h-9 flex-1" data-id="element-323" data-mp="1nauhqmcmfffx">
                  <span aria-hidden="true" className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-rule" data-id="element-324" data-mp="1n9jaudnvprkl" />
                  <span className={`absolute top-1/2 h-2.5 -translate-y-1/2 rounded-pill ${isYou ? 'bg-primary' : overlap ? 'bg-primary-text' : 'bg-ink-subtle'}`} style={position(member.years)} data-id="element-325" data-mp="2frewg765qoq4" />
                  <span className="sr-only" data-id="element-326" data-mp="d6cjxpnmf6ig">
                    {member.years}
                    {overlap && !isYou ? `, overlapping your years from ${overlap.start} to ${overlap.end}` : ''}
                  </span>
                </span>

                <span className="hidden w-24 shrink-0 text-right text-small text-ink-muted md:inline" data-id="element-327" data-mp="1138f1bt4ls6e">
                  {member.years}
                </span>
              </button>
            </li>;
      })}
      </ol>

      <p className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-rule pt-4 text-small text-ink-muted" data-id="element-328" data-mp="1tkuk08c91uiw">
        <span className="flex items-center gap-2" data-id="element-329" data-mp="1orrpnxzneh1n">
          <span aria-hidden="true" className="h-2.5 w-6 rounded-pill bg-primary" data-id="element-330" data-mp="1x28j47kuk5i1" />
          You
        </span>
        <span className="flex items-center gap-2" data-id="element-331" data-mp="212k5ppwrsfw5">
          <span aria-hidden="true" className="h-2.5 w-6 rounded-pill bg-primary-text" data-id="element-332" data-mp="21558ch4w6zuy" />
          Here when you were
        </span>
        <span className="flex items-center gap-2" data-id="element-333" data-mp="2b217vxctcbnd">
          <span aria-hidden="true" className="h-2.5 w-6 rounded-pill bg-ink-subtle" data-id="element-334" data-mp="2b2t5tetvulgt" />
          Different years
        </span>
      </p>
    </div>;
}